import { type FC, useState, useEffect } from "react";
import NavbarSidebarLayout from "../../layouts/navbar-sidebar";
import { useContract } from "../../contexts/ContractContext";
import { Select, Button, Alert, Breadcrumb, BreadcrumbItem } from "flowbite-react";
import { HiHome } from "react-icons/hi";
import ContractCard from "../../components/contracts/ContractCard";
import AddressCustomerTables from "../../components/contracts/AddressCustomerTables";
import { supabase, TABLES } from "../../lib/supabase";
import { useAuth } from "../../contexts/AuthContext";
import { HouseNumberButtonGroup } from "../../components/contracts/address-buttons";
import { getWorkerIdForUser } from "../../services/contracts";
import { 
  setCachedCategories, 
  isCategoriesCacheValid,
  setCachedCompanies,
  isCompaniesCacheValid
} from "../../utils/contractDataCache";
import { getContractCategories } from "../../services/contract-categories";
import { getContractCompanies } from "../../services/contract-companies";

const ContractCreationPage: FC = function () {
  const { user } = useAuth();
  const { tempAddress, contractData, updateContractData, setTempAddress } =
    useContract();
  const [showContractCard, setShowContractCard] = useState(
    contractData.customerState === "Auftrag"
  );
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [addressId, setAddressId] = useState<number | undefined>();

  // Fetch or create address ID when tempAddress changes
  useEffect(() => {
    const getAddressId = async () => {
      if (!tempAddress || !user?.id) {
        setAddressId(undefined);
        return;
      }

      try {
        // Try to find existing address
        const { data: existingAddress } = await supabase
          .from(TABLES.ADDRESSES)
          .select("id")
          .eq("street", tempAddress.street)
          .eq("house_number", tempAddress.house_number)
          .eq("zip_code", tempAddress.zip_code)
          .single();

        if (existingAddress) {
          setAddressId(existingAddress.id);
          return;
        }

        // If not found, create new address
        const { data: newAddress, error: createError } = await supabase
          .from(TABLES.ADDRESSES)
          .insert({
            street: tempAddress.street,
            house_number: tempAddress.house_number,
            zip_code: tempAddress.zip_code,
            city: tempAddress.city,
            country: tempAddress.country,
            latitude: tempAddress.latitude,
            longitude: tempAddress.longitude,
            created_by_user_id: user.id,
          })
          .select()
          .single();

        if (createError) throw createError;
        if (newAddress) setAddressId(newAddress.id);
      } catch (error) {
        console.error("Error getting address ID:", error);
      }
    };

    getAddressId();
  }, [tempAddress, user?.id]);

  const handleFloorSelect = (floor: string) => {
    updateContractData({ floor });
  };

  const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    updateContractData({ name: e.target.value });
  };

  // Background cache preloading for categories and companies
  const preloadContractData = async () => {
    try {
      // Check if categories are already cached and valid
      if (!isCategoriesCacheValid()) {
        const categories = await getContractCategories();
        setCachedCategories(categories);
      }

      // Check if companies are already cached and valid
      if (!isCompaniesCacheValid()) {
        const companies = await getContractCompanies();
        setCachedCompanies(companies);
      }
    } catch (error) {
      console.warn("Failed to preload contract data:", error);
    }
  };

  const handleCustomerStateSelect = (state: string) => {
    updateContractData({ customerState: state });
    setShowContractCard(state === "Auftrag");
    
    // Start background preloading when "Auftrag" is selected
    if (state === "Auftrag") {
      preloadContractData();
    }
    
    // Reset save states when changing customer state
    setSaveError(null);
    setSaveSuccess(false);
  };

  // Initialize contract data and state when component mounts
  useEffect(() => {
    setShowContractCard(contractData.customerState === "Auftrag");
  }, [contractData.customerState]);

  const handleCommentsChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    updateContractData({ comments: e.target.value });
  };

  const getFloorNumber = (floor: string): string => {
    if (floor === "EG") return "0";
    if (floor.endsWith(".")) {
      return floor.replace(".", "");
    }
    return floor;
  };

  const handleSave = async () => {
    if (!tempAddress) {
      setSaveError("Keine Adresse ausgewählt");
      return;
    }

    if (!user?.id) {
      setSaveError("Benutzer nicht angemeldet");
      return;
    }

    // Add validation for empty name
    if (!contractData.name.trim()) {
      setSaveError("Bitte geben Sie einen Kundennamen ein");
      return;
    }

    setIsSaving(true);
    setSaveError(null);
    setSaveSuccess(false);

    try {
      // Get worker ID for the current user
      const workerId = await getWorkerIdForUser(user.id);

      // Then save the customer data with worker ID
      const { error: customerError } = await supabase
        .from(TABLES.CUSTOMERS)
        .insert({
          first_name: "", // Empty for non-contract customers
          last_name: contractData.name,
          floor: getFloorNumber(contractData.floor),
          notes: contractData.comments,
          address_id: addressId,
          interest_level: contractData.customerState,
          assigned_worker_id: workerId, // Add worker ID
        });

      if (customerError) throw new Error(customerError.message);

      // Reset form fields but keep floor
      updateContractData({
        name: "",
        customerState: "Nicht geöffnet",
        comments: "",
        floor: contractData.floor, // Keep the current floor
      });
      setSaveSuccess(true);
    } catch (error) {
      setSaveError(
        error instanceof Error
          ? error.message
          : "Kunde konnte nicht gespeichert werden"
      );
    } finally {
      setIsSaving(false);
    }
  };

  const additionalFloors = [
    "6.",
    "7.",
    "8.",
    "9.",
    "10.",
    "11.",
    "12.",
    "13.",
    "14.",
    "15.",
    "KG",
    "UG",
    "DG",
  ];

  const showSaveButton =
    contractData.customerState &&
    ["Nicht geöffnet", "Rückruf", "Nicht interessiert"].includes(
      contractData.customerState
    );

  return (
    <NavbarSidebarLayout pageTitle="Auftragserstellung">
      <div className="p-4">
        <div className="block items-center justify-between border-b border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-800 sm:flex lg:mt-1.5">
          <div className="mb-1 w-full">
            <div className="mb-4">
              <Breadcrumb className="mb-4">
                <BreadcrumbItem href="#">
                  <div className="flex items-center gap-x-3">
                    <HiHome className="text-xl" />
                    <span className="dark:text-white">Startseite</span>
                  </div>
                </BreadcrumbItem>
                <BreadcrumbItem>Auftragserstellung</BreadcrumbItem>
              </Breadcrumb>
              <h1 className="text-xl font-semibold text-gray-900 dark:text-white sm:text-2xl">
                Auftragserstellung
              </h1>
            </div>
          </div>
        </div>

        <div className="mt-4">
          <div className="rounded-lg bg-white p-4 shadow-sm dark:bg-gray-800">
            <h2 className="mb-4 text-lg font-semibold text-gray-900 dark:text-white">
              Neuen Auftrag erstellen
            </h2>
            {tempAddress && (
              <div className="mb-6 rounded-lg border border-gray-200 bg-gray-50 p-4 dark:border-gray-700 dark:bg-gray-700">
                <h3 className="mb-3 text-base font-semibold text-gray-900 dark:text-white">
                  Ausgewählte Adresse
                </h3>
                <div className="space-y-1 text-sm text-gray-600 dark:text-gray-300">
                  <div className="flex items-center space-x-2">
                    <p>
                      {tempAddress.street} {tempAddress.house_number}
                    </p>
                    <HouseNumberButtonGroup
                      tempAddress={tempAddress}
                      user={user}
                      onAddressUpdate={setTempAddress}
                    />
                  </div>
                  <p>
                    {tempAddress.zip_code} {tempAddress.city}
                  </p>
                  <p>{tempAddress.country}</p>
                </div>
              </div>
            )}
            <div className="space-y-6">
              {/* Name Input */}
              <div>
                <label
                  htmlFor="name"
                  className="mb-2 block text-sm font-medium text-gray-900 dark:text-white"
                >
                  Kundenname
                </label>
                <input
                  type="text"
                  id="name"
                  value={contractData.name}
                  onChange={handleNameChange}
                  className="block w-full rounded-lg border border-gray-300 bg-gray-50 p-2.5 text-sm text-gray-900 focus:border-primary-500 focus:ring-primary-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white dark:placeholder:text-gray-400 dark:focus:border-primary-500 dark:focus:ring-primary-500"
                  placeholder="Kundenname eingeben"
                />
              </div>

              {/* Floor Selection */}
              <div>
                <label
                  htmlFor="floor-select"
                  className="mb-2 block text-sm font-medium text-gray-900 dark:text-white"
                >
                  Etage
                </label>
                <div className="flex flex-wrap items-center gap-2">
                  {["EG", "1.", "2.", "3.", "4.", "5."].map((floor) => (
                    <button
                      key={floor}
                      onClick={() => handleFloorSelect(floor)}
                      className={`rounded-lg px-4 py-2 text-sm font-medium ${
                        contractData.floor === floor
                          ? "bg-primary-700 text-white"
                          : "bg-gray-100 text-gray-900 hover:bg-gray-200 dark:bg-gray-700 dark:text-white dark:hover:bg-gray-600"
                      }`}
                    >
                      {floor}
                    </button>
                  ))}
                  <Select
                    id="floor-select"
                    value={
                      additionalFloors.includes(contractData.floor)
                        ? contractData.floor
                        : ""
                    }
                    onChange={(e) => handleFloorSelect(e.target.value)}
                    className="!w-auto"
                  >
                    <option value="">Weitere Etagen...</option>
                    {additionalFloors.map((floor) => (
                      <option key={floor} value={floor}>
                        {floor}
                      </option>
                    ))}
                  </Select>
                </div>
                {contractData.floor && (
                  <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
                    Ausgewählte Etage: {contractData.floor}
                  </p>
                )}
              </div>

              {/* Customer State Selection */}
              <div>
                <label
                  htmlFor="customer-state"
                  className="mb-2 block text-sm font-medium text-gray-900 dark:text-white"
                >
                  Kundenstatus
                </label>
                <div className="flex flex-wrap gap-2">
                  {[
                    "Nicht geöffnet",
                    "Rückruf",
                    "Nicht interessiert",
                    "Auftrag",
                  ].map((state) => (
                    <button
                      key={state}
                      id={
                        state === contractData.customerState
                          ? "customer-state"
                          : undefined
                      }
                      onClick={() => handleCustomerStateSelect(state)}
                      className={`rounded-lg px-4 py-2 text-sm font-medium ${
                        contractData.customerState === state
                          ? "bg-primary-700 text-white"
                          : "bg-gray-100 text-gray-900 hover:bg-gray-200 dark:bg-gray-700 dark:text-white dark:hover:bg-gray-600"
                      }`}
                    >
                      {state}
                    </button>
                  ))}
                </div>
                {contractData.customerState && (
                  <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
                    Ausgewählter Status: {contractData.customerState}
                  </p>
                )}
              </div>

              {/* Comments Field */}
              <div>
                <label
                  htmlFor="comments"
                  className="mb-2 block text-sm font-medium text-gray-900 dark:text-white"
                >
                  Kommentare
                </label>
                <textarea
                  id="comments"
                  rows={4}
                  value={contractData.comments}
                  onChange={handleCommentsChange}
                  className="block w-full rounded-lg border border-gray-300 bg-gray-50 p-2.5 text-sm text-gray-900 focus:border-primary-500 focus:ring-primary-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white dark:placeholder:text-gray-400 dark:focus:border-primary-500 dark:focus:ring-primary-500"
                  placeholder="Kommentare zum Kunden eingeben..."
                />
              </div>

              {/* Save Button (only for non-contract states) */}
              {showSaveButton && (
                <div className="flex flex-col gap-4">
                  <Button
                    color="primary"
                    onClick={handleSave}
                    disabled={isSaving}
                  >
                    {isSaving ? "Wird gespeichert..." : "Kunde speichern"}
                  </Button>
                  {saveError && (
                    <Alert color="failure">
                      Fehler beim Speichern: {saveError}
                    </Alert>
                  )}
                  {saveSuccess && (
                    <Alert color="success">Kunde erfolgreich gespeichert</Alert>
                  )}
                </div>
              )}

              {/* Contract Card */}
              {contractData.customerState === "Auftrag" && showContractCard && (
                <ContractCard />
              )}
            </div>
          </div>
        </div>

        {/* Customer Tables */}
        {addressId && <AddressCustomerTables addressId={addressId} />}
      </div>
    </NavbarSidebarLayout>
  );
};

export default ContractCreationPage;
